import React, { memo, useContext, useEffect, useState } from "react";
import RedeemIcon from "@material-ui/icons/Redeem";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { InputWithIcon } from "../input-with-icon/input-with-icon";
import { VoucherInputCtx } from "../details/details-vouchers/details-vouchers";
import { useDetailsVoucherContext } from "../../contexts/commercial-context";
import { checkVoucherCode } from "../../requests/pending-booking.requests";
import { PendingBooking } from "../../types";
import { pendingBookingStore } from "../../stores/pending-booking.store";
import { Loader } from "../loader/loader";
import { ValidationErrorApiErrorImpl } from "../../services/api-errors/api-errors-impl";
import { useErrorHandler } from "react-error-boundary";
import { BackgroundCircleButton } from "../background-circle-button/background-circle-button";

const InputWrapper = styled.div`
  display: flex;
  align-items: center;
  color: ${(props) => props.theme.primaryColor};

  &.disabled {
    pointer-events: none;
    opacity: 0.4;
  }

  .input-with-icon {
    width: inherit;

    @media screen and (max-width: 368px) {
      width: 250px;
    }
  }
`;

const GolVoucherInput = () => {
  const { t } = useTranslation();
  const [code, setCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [validateCode, setValidateCode] = useState(false);
  const voucherCtx = useContext(VoucherInputCtx);
  const { updateMessageError, messageError, updateVouchers, voucherAllowed } =
    voucherCtx;
  const { updateDisabledBtnFromVoucher, updateValidVoucherCode } =
    useDetailsVoucherContext();

  const voucherCodeChanged = (ev: any) => {
    updateMessageError("");
    const { value } = ev.target;
    setCode(value);
  };

  const onCheckVoucherCodeClick = () => {
    const selectedVouchers = pendingBookingStore.selectedVouchers;
    if (
      selectedVouchers.filter((voucher) => voucher.code === code).length > 0
    ) {
      updateMessageError(t(`instabook.gift_code.already_added`));
      return setValidateCode(false);
    }
    if (voucherAllowed) {
      setValidateCode(true);
    }
  };

  const hasError = messageError ? messageError.length !== 0 : false;
  const throwError = useErrorHandler();
  useEffect(() => {
    updateDisabledBtnFromVoucher(code.length !== 0);
  }, [code]);

  useEffect(() => {
    setIsLoading(validateCode);
    if (validateCode) {
      const subscription = checkVoucherCode(code).subscribe({
        next: (resp: PendingBooking) => {
          pendingBookingStore.pendingBooking = resp;
          setCode("");
          updateVouchers(resp.vouchers);
          setIsLoading(false);
          setValidateCode(false);
          updateValidVoucherCode(true);
        },
        error: (error) => {
          setValidateCode(false);
          if (error instanceof ValidationErrorApiErrorImpl) {
            const _getVoucherDetailsError = error.getVoucherDetailsError();
            if (_getVoucherDetailsError !== undefined) {
              updateMessageError(
                error.response.data["error"][
                  _getVoucherDetailsError!.toString()
                ][0]
              );
            }
          } else {
            throwError(error);
          }
        },
      });
      return () => {
        subscription.unsubscribe();
      };
    }
  }, [validateCode]);

  return (
    <>
      {isLoading && <Loader />}
      <InputWrapper
        data-test="voucher-input-wrapper"
        className={isLoading ? "disabled" : ""}
      >
        <InputWithIcon
          error={hasError}
          name={"voucher"}
          readOnly={false}
          value={code}
          placeholderKey={"instabook.gift_card.placeholder"}
          variant={"outlined"}
          onChange={voucherCodeChanged}
          icon={<RedeemIcon />}
        />
        <BackgroundCircleButton
          onClick={onCheckVoucherCodeClick}
          disabled={hasError ? true : code.length === 0}
        />
      </InputWrapper>
    </>
  );
};

export const VoucherInput = memo(GolVoucherInput);
